import React from 'react';
import { Shield, TrendingUp, Lock, AlertCircle, Globe, Award, Users, Building } from 'lucide-react';

const BenefitsSection = () => {
  const benefits = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Grants you exclusive nationwide rights over your mark and provides a legal basis to stop others from using similar or misleading branding'
    },
    {
      icon: TrendingUp,
      title: 'Brand Equity',
      description: 'Strengthens brand awareness and customer loyalty, turning your identity into measurable business value and stronger market positioning'
    },
    {
      icon: Lock,
      title: 'Asset Value',
      description: 'A trademark functions as an intellectual asset that can be licensed, transferred, or leveraged as financial security, increasing overall company worth'
    },
    {
      icon: AlertCircle,
      title: 'Legal Presumption',
      description: 'Federal registration establishes official proof of ownership and validity, making it significantly easier to enforce your rights'
    },
    {
      icon: Globe,
      title: 'National Coverage',
      description: 'Provides protection across the entire country, not limited to specific regions where your business currently operates'
    },
    {
      icon: Award,
      title: 'Competitive Edge',
      description: 'Helps your brand stand apart in competitive markets by securing a unique identity that others cannot lawfully copy'
    },
    {
      icon: Users,
      title: 'Customer Trust',
      description: 'Improves buyer confidence by signaling authenticity, professionalism, and reliability through a registered brand identity'
    },
    {
      icon: Building,
      title: 'Business Growth',
      description: 'Supports expansion strategies such as franchising, partnerships, and licensing by building a legally protected brand foundation'
    }
  ];

  return (
    <section className="py-20 bg-white" aria-labelledby="benefits-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Powerful Advantages
          </div>
          <h2 id="benefits-heading" className="text-4xl md:text-5xl font-bold text-black mb-6">
            Benefits of Trademark Registration
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Securing a registered trademark delivers full-scale protection and unlocks business opportunities that unregistered brands are unable to access
          </p>
        </div>

        {/* Benefits Grid - Horizontal scroll on mobile */}
        <div className="lg:grid lg:grid-cols-4 md:grid md:grid-cols-2 gap-8 hidden md:grid">
          {/* Desktop Grid */}
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
            >
              {/* Icon */}
              <div className="w-14 h-14 bg-gradient-to-br from-[#168356] to-[#0a5938] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                <benefit.icon className="w-7 h-7 text-white" />
              </div>
              
              {/* Content */}
              <h3 className="text-xl font-bold text-black mb-3 group-hover:text-[#168356] transition-colors">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>

              {/* Decorative element */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#168356]/5 rounded-full blur-2xl group-hover:bg-[#168356]/10 transition-all duration-300"></div>
            </div>
          ))}
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4">
          <div className="flex gap-6" style={{ width: 'max-content' }}>
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 flex-shrink-0"
                style={{ width: '280px' }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#168356] to-[#0a5938] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default BenefitsSection;